/* ============================================================
   Ipromea Pet Health Quiz — v2.2
   Changes from v2.1:
   - Contact card updated: 25% off incentive copy
   - Added mobile phone field (optional)
   - Added SMS consent checkbox (shown when phone entered)
   - Phone number sent to Klaviyo profile (E.164 format)
   - SMS subscription added to Klaviyo list on opt-in
   - Phone number saved to Supabase submissions
   ============================================================ */
(function () {
  'use strict';

  /* ── Configuration ────────────────────────────────────────*/
  const IQ_CONFIG = {
    SUPABASE_URL:       'https://vhlanhgqhjbqbtmpwndj.supabase.co',
    SUPABASE_ANON_KEY:  'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InZobGFuaGdxaGpicWJ0bXB3bmRqIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzgzOTE0NDksImV4cCI6MjA5Mzk2NzQ0OX0.P-C0cBC5X4-j2KsgadlMICc3dv2PbguyTZChH-Y6lPs',
    KLAVIYO_PUBLIC_KEY: 'WLPBy9',
    KLAVIYO_LIST_ID:    'Y2Zhwt',
  };

  /* ── Products ─────────────────────────────────────────────*/
  const PRODUCTS = {
    tummy_time: {
      id: 'tummy_time', name: 'Tummy Time', subtitle: 'Liquid Probiotics for Dogs & Cats',
      description: 'A broad-spectrum probiotic broth powered by our patented Zoonatant™ postbiotic technology. Pour over any meal to support gut balance, digestion and immunity from the inside out.',
      benefits: ['Settles runny stools and diarrhoea', 'Reduces vomiting and nausea', 'Boosts gut immunity', 'Restores healthy gut bacteria balance'],
      url: '/products/liquid-probiotic-digestion-care', badge: 'Best for Digestion', badgeColor: '#2a9d5c', for: ['dog', 'cat'],
    },
    dog_stress: {
      id: 'dog_stress', name: 'Dog Stress & Anxiety Support', subtitle: 'Natural Calming Probiotic Powder',
      description: 'A targeted probiotic powder with calming probiotic strains plus hemp, chamomile and valerian root. Works on the gut-brain axis to reduce anxiety and promote a calmer, more settled dog.',
      benefits: ['Relieves separation anxiety', 'Calms during storms and fireworks', 'Supports nervous system health', 'Promotes relaxation naturally'],
      url: '/products/dog-stress-anxiety-support', badge: 'Best for Dog Anxiety', badgeColor: '#7b5ea7', for: ['dog'],
    },
    cat_stress: {
      id: 'cat_stress', name: 'Cat Stress & Anxiety Support', subtitle: 'Natural Calming Probiotic Powder',
      description: 'A targeted probiotic powder for cats, formulated with calming probiotic strains, hemp, chamomile and valerian root. Supports the gut-brain connection to ease stress and settle anxious cats.',
      benefits: ['Eases anxiety and stress', 'Supports the gut-brain axis', 'Calms during travel and vet visits', 'Promotes inner calm naturally'],
      url: '/products/cat-stress-anxiety-support', badge: 'Best for Cat Anxiety', badgeColor: '#7b5ea7', for: ['cat'],
    },
    collagen_care: {
      id: 'collagen_care', name: 'Collagen Rich Skin & Coat Care', subtitle: 'Marine Collagen + Probiotic Meal Topper',
      description: 'A pre/probiotic collagen meal topper with 50% marine collagen and 10 billion good bacteria per serve. Reduces dryness, itchiness and shedding while boosting coat lustre, joint health and overall vitality.',
      benefits: ['Lustrous, shiny coat', 'Reduces itching and excess shedding', 'Nourishes skin and reduces dryness', 'Strengthens nails, bones and joints'],
      url: '/products/collagen-rich-skin-coat-care-dogs-and-cats', badge: 'Skin, Coat & Joint', badgeColor: '#d4872a', for: ['dog', 'cat'],
    },
    dog_absorb: {
      id: 'dog_absorb', name: 'Dog Detox & Digestive Balance', subtitle: 'Digestive & Liver Support Probiotic',
      description: 'A pre, pro and postbiotic meal topper that rebalances the gut microbiome, supports the gut-brain connection and helps the body manage toxins and digestive stress. Includes milk thistle and beetroot.',
      benefits: ['Rebalances gut microbiome', 'Reduces gut dysbiosis and intestinal disease', 'Supports mood and behaviour', 'Improves nutrient absorption and energy'],
      url: '/products/dog-detox-and-digestive-balance', badge: 'Digestive Support', badgeColor: '#2187c9', for: ['dog'],
    },
    dog_move: {
      id: 'dog_move', name: 'Dog Joint Mobility & Support', subtitle: 'Probiotic & Petagile® Collagen Powder',
      description: "A probiotic meal topper with clinically-studied Petagile® collagen to support joint health and maintain your dog's active lifestyle. Reduces inflammation and promotes cartilage regeneration.",
      benefits: ['Enhances joint health and mobility', 'Promotes cartilage regeneration', 'Reduces joint inflammation', 'Supports an active lifestyle at every age'],
      url: '/products/dog-joint-mobility-support', badge: 'Best for Joints', badgeColor: '#c0392b', for: ['dog'],
    },
    probiotic_dog_treats: {
      id: 'probiotic_dog_treats', name: 'Probiotic Dog Treats', subtitle: 'High-Protein Kangaroo Treats with Probiotics',
      description: 'All-natural, high-protein Australian kangaroo treats packed with billions of live probiotic cells. Supports gut health, immunity and dental hygiene.',
      benefits: ['Billions of probiotics per bite', 'Lean single-source kangaroo protein', 'Supports gut health and immunity', 'Low fat and no artificial additives'],
      url: '/products/probiotic-dog-treats', badge: 'Probiotic Treats', badgeColor: '#e07b39', for: ['dog'],
    },
    probiotic_cat_treats: {
      id: 'probiotic_cat_treats', name: 'Probiotic Cat Treats', subtitle: 'High-Protein Kangaroo Treats for Cats',
      description: 'Functional probiotic kangaroo treats designed for cats, fortified with billions of probiotic bacterial cells to support gut health, immunity and coat condition.',
      benefits: ['Fortified with billions of probiotics', 'Supports feline gut health', 'All-natural kangaroo protein', 'Boosts immune defence'],
      url: '/products/probiotic-cat-treats', badge: 'Cat Treats', badgeColor: '#BD1557', for: ['cat'],
    },
    chews_large: {
      id: 'chews_large', name: '2-in-1 Oral & Gut Chews — Large Dogs', subtitle: 'Probiotic Dental Chews for Large Breeds',
      description: 'A daily dental chew for large dogs combining an abrasive cleaning texture with oral-specific probiotic strains to fight plaque, freshen breath and support gut health.',
      benefits: ['Fights plaque and tartar daily', 'Freshens breath with oral probiotics', 'Supports gut microbiome', 'Sized for large breed dogs over 10kg'],
      url: '/products/2-in-1-oral-and-gut-chews-for-large-dogs', badge: 'Dental — Large Dogs', badgeColor: '#2a7a4f', for: ['dog'],
    },
    chews_small: {
      id: 'chews_small', name: '2-in-1 Oral & Gut Chews — Small Dogs', subtitle: 'Probiotic Dental Chews for Small Breeds',
      description: 'A daily dental chew for small dogs combining an abrasive cleaning texture with oral-specific probiotic strains to fight plaque, freshen breath and support gut health.',
      benefits: ['Fights plaque and tartar daily', 'Freshens breath with oral probiotics', 'Supports gut microbiome', 'Sized for small breed dogs under 10kg'],
      url: '/products/2-in-1-oral-and-gut-chews-for-small-dogs', badge: 'Dental — Small Dogs', badgeColor: '#2a7a4f', for: ['dog'],
    },
  };

  /* ── Questions ────────────────────────────────────────────*/
  const QUESTIONS = [
    { id: 'pet_type', type: 'card_select', question: 'What type of pet do you have?', subtitle: "We'll tailor your recommendations specifically for them.", single: true,
      options: [{ value: 'dog', label: 'Dog', icon: '🐶', description: 'Canine companion' }, { value: 'cat', label: 'Cat', icon: '🐱', description: 'Feline friend' }] },
    { id: 'pet_name', type: 'text_input', question: "What's your pet's name?", subtitle: "We'd love to make this personal for them.", placeholder: 'e.g. Buddy, Luna, Biscuit...', optional: true },
    { id: 'pet_age', type: 'card_select', question: 'How old is {name}?', subtitle: 'Age helps us recommend the right formula and dosage.', single: true,
      options: [{ value: 'puppy', label: 'Under 1 year', icon: '🍼', description: 'Puppy or kitten' }, { value: 'young', label: '1 to 3 years', icon: '⚡', description: 'Young and energetic' }, { value: 'adult', label: '3 to 8 years', icon: '🌟', description: 'In their prime' }, { value: 'senior', label: '8+ years', icon: '👑', description: 'Senior and wise' }] },
    { id: 'pet_size', type: 'card_select', question: 'How big is {name}?', subtitle: 'This helps us recommend the correct product size for chews and dosing.', single: true, showIf: (a) => a.pet_type === 'dog',
      options: [{ value: 'small', label: 'Small', icon: '🐾', description: 'Under 10 kg' }, { value: 'medium', label: 'Medium', icon: '🐾', description: '10 to 25 kg' }, { value: 'large', label: 'Large', icon: '🐾', description: 'Over 25 kg' }] },
    { id: 'concerns', type: 'card_select', question: "What are {name}'s main health concerns?", subtitle: "Select all that apply — we'll build a tailored plan.", single: false, minSelect: 1,
      options: [
        { value: 'digestion', label: 'Digestive Issues', icon: '🪲', description: 'Runny stools, vomiting, diarrhoea' },
        { value: 'anxiety', label: 'Anxiety & Stress', icon: '😰', description: 'Separation, storms, travel, nervousness' },
        { value: 'skin_coat', label: 'Skin, Coat & Nails', icon: '✨', description: 'Itching, shedding, dull coat, dryness' },
        { value: 'joints', label: 'Joints & Mobility', icon: '🦵', description: 'Stiffness, limping, difficulty moving' },
        { value: 'energy', label: 'Low Energy', icon: '⚡', description: 'Lethargy, poor nutrient absorption' },
        { value: 'immunity', label: 'Immune Support', icon: '🛡️', description: 'Frequently unwell, post-illness recovery' },
        { value: 'liver', label: 'Liver & Organ Health', icon: '💊', description: 'Liver support, toxin exposure, medication' },
        { value: 'dental', label: 'Bad Breath & Dental', icon: '🦷', description: 'Teeth, gums, tartar, fresh breath' },
        { value: 'wellness', label: 'General Wellness', icon: '🌿', description: 'Just want to keep {name} healthy' },
      ] },
    { id: 'digestion_detail', type: 'card_select', question: "How often does {name} have digestive issues?", subtitle: 'This helps us match the right level of gut support.', showIf: (a) => a.concerns && a.concerns.includes('digestion'), single: true,
      options: [{ value: 'occasional', label: 'Occasionally', icon: '🟡', description: 'Once in a while' }, { value: 'frequent', label: 'Frequently', icon: '🟠', description: 'A few times a week' }, { value: 'constant', label: 'Constantly', icon: '🔴', description: 'Daily or ongoing' }, { value: 'post_antibiotic', label: 'After Antibiotics', icon: '💊', description: 'Recovering from medication' }] },
    { id: 'skin_detail', type: 'card_select', question: "What best describes {name}'s skin or coat concern?", subtitle: 'Helps us choose between topical and internal support.', showIf: (a) => a.concerns && a.concerns.includes('skin_coat'), single: false,
      options: [{ value: 'itching', label: 'Itching & Scratching', icon: '😣', description: 'Constant scratching or licking' }, { value: 'shedding', label: 'Excessive Shedding', icon: '🌀', description: 'More fur than usual on furniture' }, { value: 'dull_coat', label: 'Dull or Dry Coat', icon: '🐾', description: 'Lacks shine or feels rough' }, { value: 'dry_skin', label: 'Dry or Flaky Skin', icon: '❄️', description: 'Dandruff or flaking' }, { value: 'allergies', label: 'Skin Allergies', icon: '🌿', description: 'Reactions to food or environment' }, { value: 'nails', label: 'Weak or Brittle Nails', icon: '✂️', description: 'Breaking or splitting nails' }] },
    { id: 'anxiety_detail', type: 'card_select', question: "What triggers {name}'s anxiety?", subtitle: 'Select all that apply.', showIf: (a) => a.concerns && a.concerns.includes('anxiety'), single: false,
      options: [{ value: 'separation', label: 'Separation', icon: '🚪', description: 'When you leave home' }, { value: 'storms', label: 'Thunder & Lightning', icon: '⛈️', description: 'Weather events' }, { value: 'fireworks', label: 'Fireworks & Loud Noises', icon: '🎆', description: 'Bangs and surprises' }, { value: 'travel', label: 'Travel & Car Rides', icon: '🚗', description: 'Getting from A to B' }, { value: 'new_environment', label: 'New Places or Visitors', icon: '🏠', description: 'Changes to routine or environment' }, { value: 'general', label: 'Generally Nervous', icon: '😟', description: 'Anxious most of the time' }] },
    { id: 'joint_detail', type: 'card_select', question: "How is {name}'s mobility affected?", subtitle: 'Helps us pick the right level of joint support.', showIf: (a) => a.concerns && a.concerns.includes('joints'), single: true,
      options: [{ value: 'mild', label: 'Mild Stiffness', icon: '🟡', description: 'Occasional slowness, especially mornings' }, { value: 'moderate', label: 'Noticeable Limping', icon: '🟠', description: 'Favours a leg or slows on walks' }, { value: 'severe', label: 'Difficulty Moving', icon: '🔴', description: 'Struggles to stand or climb stairs' }, { value: 'prevention', label: 'Prevention Only', icon: '🟢', description: 'Healthy joints now, want to protect them' }] },
  ];

  /* ── Recommendation engine ────────────────────────────────*/
  function getRecommendations(answers) {
    const scores = {};
    Object.keys(PRODUCTS).forEach((id) => (scores[id] = 0));
    const petType = answers.pet_type, age = answers.pet_age, size = answers.pet_size, concerns = answers.concerns || [];
    const eligible = Object.values(PRODUCTS).filter((p) => p.for.includes(petType));
    const eligibleIds = new Set(eligible.map((p) => p.id));
    function add(id, pts) { if (eligibleIds.has(id)) scores[id] = (scores[id] || 0) + pts; }

    concerns.forEach((concern) => {
      switch (concern) {
        case 'digestion':
          add('tummy_time', 10); if (petType === 'dog') add('dog_absorb', 6);
          if (answers.digestion_detail === 'post_antibiotic') { add('tummy_time', 4); if (petType === 'dog') add('dog_absorb', 3); }
          if (answers.digestion_detail === 'constant') add('tummy_time', 5); break;
        case 'anxiety':
          if (petType === 'dog') add('dog_stress', 12); if (petType === 'cat') add('cat_stress', 12); add('tummy_time', 2); break;
        case 'skin_coat': {
          add('collagen_care', 10); add('tummy_time', 3);
          const sd = answers.skin_detail || [];
          if (sd.includes('nails') || sd.includes('dull_coat')) add('collagen_care', 4);
          break;
        }
        case 'joints':
          if (petType === 'dog') { add('dog_move', 12); add('collagen_care', 4); } else { add('collagen_care', 10); }
          if (answers.joint_detail === 'severe') { add('dog_move', 5); add('tummy_time', 2); }
          if (answers.joint_detail === 'prevention') add('collagen_care', 4); break;
        case 'energy': if (petType === 'dog') add('dog_absorb', 10); add('tummy_time', 4); break;
        case 'immunity': add('tummy_time', 4); if (petType === 'dog') add('probiotic_dog_treats', 2); if (petType === 'cat') add('probiotic_cat_treats', 2); break;
        case 'liver': if (petType === 'dog') add('dog_absorb', 12); add('tummy_time', 4); break;
        case 'dental':
          if (petType === 'dog') {
            if (size === 'small') add('chews_small', 14);
            else if (size === 'large') add('chews_large', 14);
            else { add('chews_large', 12); add('chews_small', 4); }
            add('probiotic_dog_treats', 5);
          }
          if (petType === 'cat') { add('probiotic_cat_treats', 8); add('tummy_time', 4); } break;
        case 'wellness':
          add('tummy_time', 6);
          if (petType === 'dog') add('probiotic_dog_treats', 4); if (petType === 'cat') add('probiotic_cat_treats', 6); break;
      }
    });

    if (age === 'senior') { add('dog_move', 4); add('collagen_care', 3); add('tummy_time', 2); }
    if (age === 'puppy') { add('tummy_time', 4); if (petType === 'dog') add('probiotic_dog_treats', 2); if (petType === 'cat') add('probiotic_cat_treats', 2); }
    if (petType === 'dog' && size === 'large') { add('dog_move', 3); add('collagen_care', 2); }

    const ranked = eligible.map((p) => ({ ...p, score: scores[p.id] })).filter((p) => p.score > 0).sort((a, b) => b.score - a.score);
    if (ranked.length === 0) {
      return { primary: eligible.filter((p) => ['tummy_time', 'probiotic_dog_treats', 'probiotic_cat_treats'].includes(p.id)).slice(0, 2), secondary: [] };
    }
    return { primary: ranked.slice(0, 2), secondary: ranked.slice(2, 5) };
  }

  /* ── State ────────────────────────────────────────────────*/
  const state = { currentStep: -1, answers: {}, activeQuestions: [], recommendations: null, submissionId: null, contactSubmitted: false };
  function getActiveQuestions() { return QUESTIONS.filter((q) => !q.showIf || q.showIf(state.answers)); }
  function getPetName() { const n = state.answers.pet_name && state.answers.pet_name.trim(); return n || 'your pet'; }
  function interpolate(text) { return text.replace(/\{name\}/g, getPetName()); }

  /* ── Phone formatting utility ─────────────────────────────*/
  function formatAuPhone(raw) {
    if (!raw) return null;
    const digits = raw.replace(/\D/g, '');
    if (!digits) return null;
    // Already in international format: 61XXXXXXXXX (11 digits)
    if (digits.startsWith('61') && digits.length === 11) return '+' + digits;
    // Australian mobile: 04XXXXXXXX (10 digits)
    if (digits.startsWith('0') && digits.length === 10) return '+61' + digits.slice(1);
    // Without leading zero: 4XXXXXXXX (9 digits)
    if (digits.length === 9 && digits.startsWith('4')) return '+61' + digits;
    // Return null for unrecognised formats — don't send malformed numbers to Klaviyo
    return null;
  }

  /* ── Supabase ─────────────────────────────────────────────*/
  async function saveToSupabase(payload) {
    if (!IQ_CONFIG.SUPABASE_URL || IQ_CONFIG.SUPABASE_URL === 'YOUR_SUPABASE_URL') return null;
    try {
      const res = await fetch(`${IQ_CONFIG.SUPABASE_URL}/rest/v1/quiz_submissions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'apikey': 'sb_publishable_uvub_HWoMxqFO9aQ_oubEQ_KDsji1IZ', 'Authorization': `Bearer ${IQ_CONFIG.SUPABASE_ANON_KEY}`, 'Prefer': 'return=representation' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) { console.warn('[Ipromea Quiz] Supabase insert failed:', await res.text()); return null; }
      const rows = await res.json();
      return rows && rows[0] ? rows[0].id : null;
    } catch (e) { console.warn('[Ipromea Quiz] Supabase error:', e); return null; }
  }

  const PRODUCT_IMAGES = {
    tummy_time: 'https://cdn.shopify.com/s/files/1/0744/6065/9880/files/ipromea-tummy-time-liquid-probiotics-main-sq.webp?v=1778327608',
    dog_stress: 'https://cdn.shopify.com/s/files/1/0744/6065/9880/files/ipromea-dog-stress-anxiety-main-sq.webp?v=1778327678',
    cat_stress: 'https://cdn.shopify.com/s/files/1/0744/6065/9880/files/ipromea-cat-stress-anxiety-main-sq_5a89e0f7-f8ec-4064-8e9a-ebfbbbdba885.webp?v=1778327749',
    collagen_care: 'https://cdn.shopify.com/s/files/1/0744/6065/9880/files/ipromea-collagen-bone-skin-coat-main-sq.webp?v=1778327373',
    dog_absorb: 'https://cdn.shopify.com/s/files/1/0744/6065/9880/files/ipromea-dog-detox-main-sq.webp?v=1778327713',
    dog_move: 'https://cdn.shopify.com/s/files/1/0744/6065/9880/files/ipromea-dog-joint-mobility-main-sq.webp?v=1778327642',
    probiotic_dog_treats: 'https://cdn.shopify.com/s/files/1/0744/6065/9880/files/ipromea-probiotic-dog_treats-front-sq.jpg?v=1778328022',
    probiotic_cat_treats: 'https://cdn.shopify.com/s/files/1/0744/6065/9880/files/ipromea-probiotic-cattreats-front-sq.jpg?v=1778327590',
    chews_large: 'https://cdn.shopify.com/s/files/1/0744/6065/9880/files/ipromea-2-in-1-oral-gut-chews-large-dogs-front1.webp?v=1778327414',
    chews_small: 'https://cdn.shopify.com/s/files/1/0744/6065/9880/files/ipromea-2-in-1-oral-gut-chews-small-dogs-front-sq.jpg?v=1778328019',
  };

  /* ── Klaviyo ──────────────────────────────────────────────*/
  async function sendToKlaviyo({ email, firstName, lastName, phone, newsletterOptIn, smsOptIn, answers, recommendations, submissionId }) {
    const key = IQ_CONFIG.KLAVIYO_PUBLIC_KEY;
    if (!key || key === 'YOUR_KLAVIYO_PUBLIC_KEY') return;
    const headers = { 'Content-Type': 'application/json', 'revision': '2023-12-15' };

    const AGE_LABELS     = { puppy: 'Under 1 year', young: '1 to 3 years', adult: '3 to 8 years', senior: '8+ years' };
    const SIZE_LABELS    = { small: 'Small (under 10 kg)', medium: 'Medium (10 to 25 kg)', large: 'Large (over 25 kg)' };
    const DIG_LABELS     = { occasional: 'Occasionally', frequent: 'Frequently (a few times a week)', constant: 'Constantly (daily or ongoing)', post_antibiotic: 'After antibiotics' };
    const ANX_LABELS     = { separation: 'Separation', storms: 'Thunder & Lightning', fireworks: 'Fireworks & Loud Noises', travel: 'Travel & Car Rides', new_environment: 'New Places or Visitors', general: 'Generally Nervous' };
    const SKIN_LABELS    = { itching: 'Itching & Scratching', shedding: 'Excessive Shedding', dull_coat: 'Dull or Dry Coat', dry_skin: 'Dry or Flaky Skin', allergies: 'Skin Allergies', nails: 'Weak or Brittle Nails' };
    const JOINT_LABELS   = { mild: 'Mild Stiffness', moderate: 'Noticeable Limping', severe: 'Difficulty Moving', prevention: 'Prevention Only (healthy now)' };

    const _concerns = answers.concerns || [];
    const _anxDetail = answers.anxiety_detail || [];
    const _skinDetail = answers.skin_detail || [];

    const properties = {
      pet_name: answers.pet_name || '', pet_type: answers.pet_type || '', pet_age: answers.pet_age || '',
      pet_size: answers.pet_size || '', concerns: _concerns.join(', '),
      digestion_detail: answers.digestion_detail || '', skin_detail: _skinDetail.join(', '),
      anxiety_detail: _anxDetail.join(', '), joint_detail: answers.joint_detail || '',
      pet_age_label:             AGE_LABELS[answers.pet_age]  || answers.pet_age  || '',
      pet_size_label:            SIZE_LABELS[answers.pet_size] || '',
      digestion_detail_label:    DIG_LABELS[answers.digestion_detail] || '',
      anxiety_detail_formatted:  _anxDetail.map((a) => ANX_LABELS[a] || a).join(', '),
      skin_detail_formatted:     _skinDetail.map((s) => SKIN_LABELS[s] || s).join(', '),
      joint_detail_label:        JOINT_LABELS[answers.joint_detail] || '',
      has_digestion: _concerns.includes('digestion'),
      has_anxiety:   _concerns.includes('anxiety'),
      has_skin_coat: _concerns.includes('skin_coat'),
      has_joints:    _concerns.includes('joints'),
      has_energy:    _concerns.includes('energy'),
      has_immunity:  _concerns.includes('immunity'),
      has_liver:     _concerns.includes('liver'),
      has_dental:    _concerns.includes('dental'),
      has_wellness:  _concerns.includes('wellness'),
      primary_recs: recommendations.primary.map((p) => p.name).join(', '),
      rec_1_name:   recommendations.primary[0] ? recommendations.primary[0].name : '',
      rec_1_image:  recommendations.primary[0] ? (PRODUCT_IMAGES[recommendations.primary[0].id] || '') : '',
      rec_1_url:    recommendations.primary[0] ? ('https://ipromea.com.au' + recommendations.primary[0].url) : '',
      rec_2_name:   recommendations.primary[1] ? recommendations.primary[1].name : '',
      rec_2_image:  recommendations.primary[1] ? (PRODUCT_IMAGES[recommendations.primary[1].id] || '') : '',
      rec_2_url:    recommendations.primary[1] ? ('https://ipromea.com.au' + recommendations.primary[1].url) : '',
      secondary_recs: recommendations.secondary.map((p) => p.name).join(', '),
      rec_3_name:  recommendations.secondary[0] ? recommendations.secondary[0].name  : '',
      rec_3_image: recommendations.secondary[0] ? (PRODUCT_IMAGES[recommendations.secondary[0].id] || '') : '',
      rec_3_url:   recommendations.secondary[0] ? ('https://ipromea.com.au' + recommendations.secondary[0].url) : '',
      rec_4_name:  recommendations.secondary[1] ? recommendations.secondary[1].name  : '',
      rec_4_image: recommendations.secondary[1] ? (PRODUCT_IMAGES[recommendations.secondary[1].id] || '') : '',
      rec_4_url:   recommendations.secondary[1] ? ('https://ipromea.com.au' + recommendations.secondary[1].url) : '',
      rec_5_name:  recommendations.secondary[2] ? recommendations.secondary[2].name  : '',
      rec_5_image: recommendations.secondary[2] ? (PRODUCT_IMAGES[recommendations.secondary[2].id] || '') : '',
      rec_5_url:   recommendations.secondary[2] ? ('https://ipromea.com.au' + recommendations.secondary[2].url) : '',
      newsletter_opt_in: newsletterOptIn,
      sms_opt_in: smsOptIn || false,
      quiz_submission_id: submissionId || '',
      quiz_completed_at: new Date().toISOString(),
      '$source': 'Ipromea Pet Health Quiz',
    };

    // Build profile attributes — include phone if provided
    const profileAttrs = { email, first_name: firstName, last_name: lastName, properties };
    if (phone) profileAttrs.phone_number = phone;

    try {
      await fetch(`https://a.klaviyo.com/client/profiles/?company_id=${key}`, {
        method: 'POST', headers,
        body: JSON.stringify({ data: { type: 'profile', attributes: profileAttrs } }),
      });
    } catch (e) { console.warn('[Ipromea Quiz] Klaviyo identify error:', e); }

    try {
      await fetch(`https://a.klaviyo.com/client/events/?company_id=${key}`, {
        method: 'POST', headers,
        body: JSON.stringify({ data: { type: 'event', attributes: { metric: { data: { type: 'metric', attributes: { name: 'Pet Health Quiz Completed' } } }, profile: { data: { type: 'profile', attributes: { email, first_name: firstName, last_name: lastName } } }, properties } } }),
      });
    } catch (e) { console.warn('[Ipromea Quiz] Klaviyo event error:', e); }

    // Email list subscription
    if (newsletterOptIn && IQ_CONFIG.KLAVIYO_LIST_ID && IQ_CONFIG.KLAVIYO_LIST_ID !== 'YOUR_KLAVIYO_LIST_ID') {
      try {
        await fetch(`https://a.klaviyo.com/client/subscriptions/?company_id=${key}`, {
          method: 'POST', headers,
          body: JSON.stringify({ data: { type: 'subscription', attributes: { profile: { data: { type: 'profile', attributes: { email, first_name: firstName, last_name: lastName } } } }, relationships: { list: { data: { type: 'list', id: IQ_CONFIG.KLAVIYO_LIST_ID } } } } }),
        });
      } catch (e) { console.warn('[Ipromea Quiz] Klaviyo email subscription error:', e); }
    }

    // SMS subscription — only if consent given and valid AU phone present
    if (smsOptIn && phone && IQ_CONFIG.KLAVIYO_LIST_ID && IQ_CONFIG.KLAVIYO_LIST_ID !== 'YOUR_KLAVIYO_LIST_ID') {
      const smsProfileAttrs = { phone_number: phone, first_name: firstName, last_name: lastName };
      if (email) smsProfileAttrs.email = email;
      try {
        await fetch(`https://a.klaviyo.com/client/subscriptions/?company_id=${key}`, {
          method: 'POST', headers,
          body: JSON.stringify({ data: { type: 'subscription', attributes: { custom_source: 'Ipromea Pet Health Quiz — SMS Opt-In', profile: { data: { type: 'profile', attributes: smsProfileAttrs } } }, relationships: { list: { data: { type: 'list', id: IQ_CONFIG.KLAVIYO_LIST_ID } } } } }),
        });
      } catch (e) { console.warn('[Ipromea Quiz] Klaviyo SMS subscription error:', e); }
    }
  }

  /* ── Contact submit ───────────────────────────────────────*/
  async function handleContactSubmit() {
    if (state.contactSubmitted) return;
    const $fname    = document.getElementById('iq-contact-fname');
    const $lname    = document.getElementById('iq-contact-lname');
    const $email    = document.getElementById('iq-contact-email');
    const $phone    = document.getElementById('iq-contact-phone');
    const $optin    = document.getElementById('iq-contact-optin');
    const $smsOptin = document.getElementById('iq-contact-sms-optin');
    const $btn      = document.getElementById('iq-contact-submit');
    const $feedback = document.getElementById('iq-contact-feedback');
    const $fields   = document.getElementById('iq-contact-fields');

    const firstName       = $fname     ? $fname.value.trim()    : '';
    const lastName        = $lname     ? $lname.value.trim()    : '';
    const email           = $email     ? $email.value.trim()    : '';
    const phoneRaw        = $phone     ? $phone.value.trim()    : '';
    const newsletterOptIn = $optin     ? $optin.checked         : false;
    const smsOptIn        = $smsOptin  ? $smsOptin.checked      : false;

    // Format phone to E.164 if provided
    const phone = phoneRaw ? formatAuPhone(phoneRaw) : null;

    if (!firstName) { showContactError('Please enter your first name.'); $fname && $fname.focus(); return; }
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { showContactError('Please enter a valid email address.'); $email && $email.focus(); return; }
    if (phoneRaw && !phone) { showContactError('Please enter a valid Australian mobile number (e.g. 0412 345 678).'); $phone && $phone.focus(); return; }

    if ($btn) { $btn.disabled = true; $btn.textContent = 'Sending...'; }
    if ($feedback) $feedback.innerHTML = '';

    const recs = state.recommendations || { primary: [], secondary: [] };

    const supabasePayload = {
      first_name: firstName, last_name: lastName || null, email,
      phone_number: phone || null, newsletter_opt_in: newsletterOptIn, sms_opt_in: smsOptIn,
      pet_type: state.answers.pet_type || null, pet_name: state.answers.pet_name || null,
      pet_age: state.answers.pet_age || null, pet_size: state.answers.pet_size || null,
      concerns: state.answers.concerns || [], digestion_detail: state.answers.digestion_detail || null,
      skin_detail: state.answers.skin_detail || [], anxiety_detail: state.answers.anxiety_detail || [],
      joint_detail: state.answers.joint_detail || null,
      primary_recs: recs.primary.map((p) => p.id), secondary_recs: recs.secondary.map((p) => p.id),
      source_url: window.location.href, submitted_at: new Date().toISOString(),
    };

    const submissionId = await saveToSupabase(supabasePayload);
    state.submissionId = submissionId;
    state.contactSubmitted = true;

    await sendToKlaviyo({ email, firstName, lastName, phone, newsletterOptIn, smsOptIn, answers: state.answers, recommendations: recs, submissionId });

    // GA4 conversion event
    if (typeof gtag === 'function') {
      gtag('event', 'quiz_recommendations_sent', {
        event_category: 'Pet Health Quiz',
        pet_type: state.answers.pet_type || '',
        primary_recommendations: recs.primary.map(function(p) { return p.name; }).join(', '),
        newsletter_opt_in: newsletterOptIn,
        sms_opt_in: smsOptIn,
      });
    }

    if ($fields) $fields.style.display = 'none';
    if ($feedback) {
      $feedback.innerHTML = `<div class="iq-contact-success"><span class="iq-contact-success-icon">✓</span><div><strong>You're in, ${firstName}!</strong><p>Your 25% off code and ${getPetName()}'s personalised plan are on their way to your inbox. Check your email shortly.</p></div></div>`;
    }
  }

  function showContactError(msg) {
    const el = document.getElementById('iq-contact-feedback');
    if (el) el.innerHTML = `<p class="iq-contact-error">${msg}</p>`;
  }

  /* ── Render ───────────────────────────────────────────────*/
  function render() {
    const container = document.getElementById('ipromea-quiz-root');
    if (!container) return;
    state.activeQuestions = getActiveQuestions();
    const totalSteps = state.activeQuestions.length;
    if (state.currentStep === -1) {
      container.innerHTML = renderWelcome();
    } else if (state.currentStep >= totalSteps) {
      if (!state.recommendations) state.recommendations = getRecommendations(state.answers);
      container.innerHTML = renderResults();
      attachContactHandlers();
    } else {
      const q = state.activeQuestions[state.currentStep];
      container.innerHTML = renderQuestion(q, state.currentStep, totalSteps);
      attachQuestionHandlers(q);
    }
  }

  function renderWelcome() {
    return `<div class="iq-screen iq-welcome"><div class="iq-welcome-inner">
     <div class="iq-logo-badge"><img src="https://cdn.shopify.com/s/files/1/0744/6065/9880/files/quiz-top.webp?v=1778887967"></div>
      <h1 class="iq-welcome-title">Find the Right <span class="iq-highlight">Ipromea</span> Products for Your Pet</h1>
      <p class="iq-welcome-sub">Answer a few quick questions and we'll build a personalised supplement plan based on your pet's unique needs. Sign up at the end to get your plan by email — plus 25% off your first order.</p>
      <ul class="iq-welcome-features">
        <li><span class="iq-check">✓</span> Takes less than 2 minutes</li>
        <li><span class="iq-check">✓</span> Personalised pet recommendations</li>
        <li><span class="iq-check">✓</span> Australian made, vet endorsed</li>
        <li><span class="iq-check">✓</span> Get 25% off your first order</li>
      </ul>
      <p>&nbsp;</p>
      <button class="iq-btn iq-btn-primary iq-start-btn" onclick="iPromeaQuiz.start()">Start the Quiz →</button>
    </div></div>`;
  }

  function renderQuestion(q, stepIndex, totalSteps) {
    const progress = Math.round(((stepIndex + 1) / totalSteps) * 100);
    const questionText = interpolate(q.question);
    const subtitleText = q.subtitle ? interpolate(q.subtitle) : '';
    let bodyHtml = '';
    if (q.type === 'card_select') {
      const selected = state.answers[q.id] || (q.single ? null : []);
      const count = q.options.length;
      const gridClass = count <= 2 ? 'iq-card-grid-2' : count <= 4 ? 'iq-card-grid-4' : 'iq-card-grid-3';
      bodyHtml = `<div class="iq-card-grid ${gridClass}">${q.options.map((opt) => {
        const isSel = q.single ? selected === opt.value : Array.isArray(selected) && selected.includes(opt.value);
        return `<button class="iq-card${isSel ? ' iq-card-selected' : ''}" data-value="${opt.value}" data-question="${q.id}" data-single="${q.single}"><span class="iq-card-icon">${opt.icon}</span><span class="iq-card-label">${opt.label}</span><span class="iq-card-desc">${interpolate(opt.description || '')}</span>${isSel ? '<span class="iq-card-check">✓</span>' : ''}</button>`;
      }).join('')}</div>${!q.single ? '<p class="iq-multi-hint">Select all that apply</p>' : ''}`;
    } else if (q.type === 'text_input') {
      bodyHtml = `<div class="iq-input-wrap"><input type="text" id="iq-text-input-${q.id}" class="iq-text-input" placeholder="${q.placeholder || ''}" value="${state.answers[q.id] || ''}" autocomplete="off" maxlength="50" /></div>`;
    }
    const canGo = canAdvance(q);
    return `<div class="iq-screen iq-question-screen">
      <div class="iq-progress-bar"><div class="iq-progress-fill" style="width:${progress}%"></div></div>
      <div class="iq-step-label">Question ${stepIndex + 1} of ${totalSteps}</div>
      <div class="iq-question-body"><h2 class="iq-question-title">${questionText}</h2>${subtitleText ? `<p class="iq-question-subtitle">${subtitleText}</p>` : ''}${bodyHtml}</div>
      <div class="iq-nav">
        ${stepIndex > 0 ? `<button class="iq-btn iq-btn-back" onclick="iPromeaQuiz.back()">← Back</button>` : '<div></div>'}
        <button class="iq-btn iq-btn-primary iq-next-btn${canGo ? '' : ' iq-btn-disabled'}" id="iq-next-btn" onclick="iPromeaQuiz.next()" ${canGo ? '' : 'disabled'}>${stepIndex === totalSteps - 1 ? 'See My Results ✨' : 'Continue →'}</button>
      </div>
    </div>`;
  }

  function canAdvance(q) {
    if (q.type === 'text_input' && q.optional) return true;
    if (q.type === 'text_input') return !!(state.answers[q.id] && state.answers[q.id].trim());
    if (q.type === 'card_select') { const v = state.answers[q.id]; if (q.single) return !!v; return Array.isArray(v) && v.length >= (q.minSelect || 1); }
    return false;
  }

  function renderResults() {
    const recs = state.recommendations;
    const name = getPetName();
    const petType = state.answers.pet_type || 'pet';
    const petEmoji = petType === 'dog' ? '🐶' : '🐱';
    const labels = { digestion: 'Digestive Health', anxiety: 'Anxiety & Stress', skin_coat: 'Skin & Coat', joints: 'Joint Mobility', energy: 'Energy & Vitality', immunity: 'Immune Support', liver: 'Liver Health', dental: 'Dental Care', wellness: 'General Wellness' };
    const concernsList = (state.answers.concerns || []).map((c) => labels[c] || c).join(', ');

    const primaryHtml = (recs.primary || []).map((p, i) => `
      <div class="iq-product-card${i === 0 ? ' iq-product-featured' : ''}">
        ${i === 0 ? `<div class="iq-featured-badge">⭐ Top Pick for ${name}</div>` : ''}
        <div class="iq-product-badge" style="background:${p.badgeColor}">${p.badge}</div>
        <h3 class="iq-product-name">${p.name}</h3>
        <p class="iq-product-subtitle">${p.subtitle}</p>
        <p class="iq-product-desc">${p.description}</p>
        <ul class="iq-product-benefits">${p.benefits.map((b) => `<li><span class="iq-benefit-check">✓</span> ${b}</li>`).join('')}</ul>
        <p>&nbsp;</p>
        <a href="${p.url}" target="_blank" class="iq-btn iq-btn-primary iq-shop-btn">Shop ${p.name} →</a>
      </div>`).join('');

    const secondaryHtml = (recs.secondary || []).length > 0 ? `
      <div class="iq-also-consider">
        <h3 class="iq-also-title">${name} May Also Benefit From</h3>
        <div class="iq-secondary-grid">${(recs.secondary || []).map((p) => `
          <div class="iq-secondary-card">
            <div class="iq-product-badge" style="background:${p.badgeColor}">${p.badge}</div>
            <h4>${p.name}</h4><p>${p.subtitle}</p>
            <a href="${p.url}" target="_blank" class="iq-btn iq-btn-outline">Learn More →</a>
          </div>`).join('')}
        </div>
      </div>` : '';

    // ── Updated contact card with 25% off incentive + SMS ──
    const contactHtml = `
      <div class="iq-contact-card">
        <div class="iq-contact-header">
          <div class="iq-contact-icon">🎁</div>
          <h3 class="iq-contact-title">Unlock 25% off your first order + get ${name}'s personalised plan</h3>
          <p class="iq-contact-sub">Enter your details to receive an exclusive 25% off discount code and ${name}'s full supplement plan straight to your inbox. No spam — unsubscribe any time.</p>
        </div>
        <div id="iq-contact-fields">
          <div class="iq-contact-row">
            <div class="iq-contact-field">
              <label for="iq-contact-fname">First name <span class="iq-required">*</span></label>
              <input type="text" id="iq-contact-fname" class="iq-text-input iq-contact-input" placeholder="Your first name" autocomplete="given-name" maxlength="80" />
            </div>
            <div class="iq-contact-field">
              <label for="iq-contact-lname">Last name</label>
              <input type="text" id="iq-contact-lname" class="iq-text-input iq-contact-input" placeholder="Your last name" autocomplete="family-name" maxlength="80" />
            </div>
          </div>
          <div class="iq-contact-field">
            <label for="iq-contact-email">Email address <span class="iq-required">*</span></label>
            <input type="email" id="iq-contact-email" class="iq-text-input iq-contact-input" placeholder="you@example.com" autocomplete="email" maxlength="254" />
          </div>
          <div class="iq-contact-field">
            <label for="iq-contact-phone">Mobile number <span style="font-size:12px;color:var(--iq-text-muted);font-weight:400;">(optional — for SMS updates)</span></label>
            <input type="tel" id="iq-contact-phone" class="iq-text-input iq-contact-input" placeholder="04XX XXX XXX" autocomplete="tel" maxlength="20" />
            <p style="margin:4px 0 0;font-size:11px;color:var(--iq-text-muted);">Australian mobile numbers only. Standard message rates apply.</p>
          </div>
          <div class="iq-contact-optin-row" id="iq-sms-optin-row">
            <input type="checkbox" id="iq-contact-sms-optin" />
            <label for="iq-contact-sms-optin">Send me ${name}'s 25% off code and exclusive health tips by SMS</label>
          </div>
          <div class="iq-contact-optin-row">
            <input type="checkbox" id="iq-contact-optin" checked />
            <label for="iq-contact-optin">Subscribe to Ipromea Pro Club for pet health tips, exclusive offers and product news</label>
          </div>
          <button id="iq-contact-submit" class="iq-btn iq-btn-primary iq-contact-btn" onclick="iPromeaQuiz.submitContact()">Unlock 25% Off + Get My Plan →</button>
          <p class="iq-contact-privacy"><a href="policies/privacy-policy">Privacy Policy</a>&nbsp; Unsubscribe any time.</p>
        </div>
        <div id="iq-contact-feedback"></div>
      </div>`;

    return `<div class="iq-screen iq-results-screen">
      <div class="iq-results-header">
        <div class="iq-results-emoji">${petEmoji}</div>
        <h2 class="iq-results-title">Here's ${name}'s Personalised Plan</h2>
        <p class="iq-results-subtitle">Based on your answers, we've selected the best Ipromea products for <strong>${name}</strong>'s needs: <em>${concernsList}</em>.</p>
      </div>
      <div class="iq-results-body">
       <div class="iq-primary-results">${contactHtml}</div>
      <div class="iq-primary-results">${primaryHtml}</div>
        ${secondaryHtml}
      </div>
      <div class="iq-results-footer">
        <div class="iq-trust-bar">
          <span>🇦🇺 Australian Made</span><span>🔬 Vet Endorsed</span><span>🐾 Dogs & Cats</span><span>✅ Natural Ingredients</span><span>🏭 TGA & cGMP Certified</span>
        </div>
        <button class="iq-btn iq-btn-outline iq-restart-btn" onclick="iPromeaQuiz.restart()">↺ Retake Quiz</button>
        <p class="iq-disclaimer">These recommendations are based on your quiz answers and are not a substitute for veterinary advice. If your pet has a diagnosed condition, please consult your veterinarian before starting any supplement.</p>
      </div>
    </div>`;
  }

  function attachContactHandlers() {
    // Submit on Enter in email field
    const $email = document.getElementById('iq-contact-email');
    if ($email) $email.addEventListener('keydown', (e) => { if (e.key === 'Enter') iPromeaQuiz.submitContact(); });
  }

  function attachQuestionHandlers(q) {
    document.querySelectorAll('.iq-card').forEach((card) => {
      card.addEventListener('click', function () {
        const value = this.dataset.value, qId = this.dataset.question, single = this.dataset.single === 'true';
        if (single) { state.answers[qId] = value; }
        else { if (!Array.isArray(state.answers[qId])) state.answers[qId] = []; const idx = state.answers[qId].indexOf(value); if (idx > -1) state.answers[qId].splice(idx, 1); else state.answers[qId].push(value); }
        document.querySelectorAll('.iq-card').forEach((c) => {
          const isSel = single ? c.dataset.value === state.answers[qId] : state.answers[qId].includes(c.dataset.value);
          c.classList.toggle('iq-card-selected', isSel);
          const ex = c.querySelector('.iq-card-check');
          if (isSel && !ex) { const ch = document.createElement('span'); ch.className = 'iq-card-check'; ch.textContent = '✓'; c.appendChild(ch); }
          else if (!isSel && ex) ex.remove();
        });
        const btn = document.getElementById('iq-next-btn');
        if (btn) { const ok = canAdvance(q); btn.disabled = !ok; btn.classList.toggle('iq-btn-disabled', !ok); }
        if (single && !q.noAutoAdvance) setTimeout(() => iPromeaQuiz.next(), 350);
      });
    });
    const inp = document.getElementById(`iq-text-input-${q.id}`);
    if (inp) {
      inp.addEventListener('input', function () { state.answers[q.id] = this.value; const btn = document.getElementById('iq-next-btn'); if (btn) { const ok = canAdvance(q); btn.disabled = !ok; btn.classList.toggle('iq-btn-disabled', !ok); } });
      inp.addEventListener('keydown', (e) => { if (e.key === 'Enter') iPromeaQuiz.next(); });
      inp.focus();
    }
  }

  /* ── Public API ───────────────────────────────────────────*/
  window.iPromeaQuiz = {
    start() { state.currentStep = 0; render(); },
    next() {
      state.activeQuestions = getActiveQuestions();
      const q = state.activeQuestions[state.currentStep];
      if (!canAdvance(q)) return;
      if (q && q.type === 'text_input') { const el = document.getElementById(`iq-text-input-${q.id}`); if (el) state.answers[q.id] = el.value; }
      state.currentStep++; render(); window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    back() { if (state.currentStep > 0) { state.currentStep--; state.activeQuestions = getActiveQuestions(); render(); window.scrollTo({ top: 0, behavior: 'smooth' }); } },
    restart() { state.currentStep = -1; state.answers = {}; state.activeQuestions = []; state.recommendations = null; state.submissionId = null; state.contactSubmitted = false; render(); window.scrollTo({ top: 0, behavior: 'smooth' }); },
    init() { state.currentStep = -1; render(); },
    submitContact() { handleContactSubmit(); },
  };

  if (document.readyState === 'loading') { document.addEventListener('DOMContentLoaded', () => window.iPromeaQuiz.init()); }
  else { window.iPromeaQuiz.init(); }
})();
